import { Link } from 'react-router';
import type { ShortcutDTO } from '@devrobot/shared';
import { ArrowRight, ExternalLink, TriangleAlert } from 'lucide-react';
import { useRecordVisit } from '../../api/queries';
import { useDesktopPwa } from '../../kit/hooks';
import { closeDrawerInstantly } from './drawer';

/** Initials of a project name, as the account avatar does for a person. */
function monogram(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const ROW = 'dr-focus group relative z-10 flex h-10 items-center gap-3 rounded-field px-3 text-sm font-medium text-base-content/70 transition-colors duration-200 hover:bg-base-content/[0.045] hover:text-base-content';

/**
 * A shortcut opens the application, so it is a real anchor and the visit is recorded without
 * preventing the default: awaiting a request before opening a tab gets it treated as a pop-up.
 * Nothing here carries `data-nav` -- the navigation glider must never land on a shortcut.
 *
 * In a desktop app window the host takes over this window instead of opening a tab. A tab from
 * an app window lands in the browser, several clicks away from the app it came from, and leaves
 * the app sitting on a page nobody is looking at; the window carries you there and the title
 * bar's Back arrow carries you home. Everywhere else -- a browser tab, a phone -- a new tab is
 * still right: there the app is one of many things open, and taking its place would lose it.
 */
function ShortcutItem({ s }: { s: ShortcutDTO }) {
  const record = useRecordVisit();
  const appWindow = useDesktopPwa();
  const broken = s.removed || s.stale;
  const why = s.removed ? 'This deployment was removed' : 'This host is no longer served by the deployment';
  // Written out, not derived: anything that is not production is marked.
  const staging = s.environment !== 'production';
  const label = staging ? `${s.projectName} · ${s.environment}` : s.projectName;

  // Non-production wears a warning ring on its monogram rather than a word beside it. The row
  // is narrow, and at icon width -- where the label is gone and the monogram is the whole row --
  // a pill had nowhere to sit, so the very view that most needs the warning was the one without
  // it. The ring inherits that view for free.
  const chip = (
    <span
      aria-hidden="true"
      className={`dr-tint-secondary flex size-[1.375rem] shrink-0 items-center justify-center rounded-field text-[0.625rem] font-semibold ${staging ? 'ring-1 ring-warning' : ''} ${broken ? 'opacity-60' : ''}`}
    >
      {monogram(s.projectName)}
    </span>
  );

  // The ring is never the whole message: a colour on an aria-hidden chip says nothing to a
  // screen reader, and says nothing to anyone who cannot separate it from the ring the broken
  // state wears. So `label` -- the same string the collapsed sidebar shows on hover -- is also
  // the link's accessible name. Spelled out rather than left to the row's own text, because
  // the name built from that text runs the monogram and the environment together into
  // "Portalstaging": each node is trimmed and the pieces are concatenated with nothing between.

  return (
    <li className="w-full lg:collapsed:tooltip lg:collapsed:tooltip-right" data-tip={broken ? `${label} — ${why}` : label}>
      {broken ? (
        // Removed or renamed: send them to the deployment, the one screen that can unpin it.
        <Link
          to={`/targets/${s.targetId}`}
          aria-label={`${label} — ${why}`}
          title={`${why}. Open the deployment to remove this shortcut.`}
          className={`${ROW} opacity-60`}
        >
          {chip}
          <span className="nav-label min-w-0 flex-1 truncate line-through decoration-base-content/30">{s.projectName}</span>
          <TriangleAlert className="nav-label size-3.5 shrink-0 text-warning lg:collapsed:hidden" aria-label={why} />
        </Link>
      ) : (
        <a
          href={s.url}
          target={appWindow ? undefined : '_blank'}
          rel="noreferrer"
          aria-label={label}
          title={s.url}
          onClick={() => {
            record.mutate({ targetId: s.targetId, url: s.url });
            // A shortcut leaves for a host rather than changing route, so the drawer's own
            // close effect (keyed on the pathname) never runs. Closed without animating:
            // the page is about to be hidden, and WebKit freezes transitions that are still
            // running when that happens.
            closeDrawerInstantly();
          }}
          className={ROW}
        >
          {chip}
          <span className="nav-label min-w-0 flex-1 truncate">{s.projectName}</span>
          {/* The arrow says where the click lands: onward inside this window, or out to a tab. */}
          {appWindow ? (
            <ArrowRight className="nav-label size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60 lg:collapsed:hidden" aria-hidden="true" />
          ) : (
            <ExternalLink className="nav-label size-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60 lg:collapsed:hidden" aria-hidden="true" />
          )}
        </a>
      )}
    </li>
  );
}

/**
 * The hosts this person pinned, above the main navigation. The section exists only while they
 * have one: an empty Shortcuts heading would be a permanent reminder of a feature nobody used.
 */
export function SidebarShortcuts({ items }: { items: ShortcutDTO[] }) {
  if (items.length === 0) return null;
  return (
    <nav aria-label="Shortcuts" className="pb-5">
      <p className="nav-label px-3 pb-1.5 text-[11px] font-semibold tracking-[0.08em] text-base-content/45 uppercase">Shortcuts</p>
      <ul className="space-y-0.5">
        {items.map((s) => (
          <ShortcutItem key={s.id} s={s} />
        ))}
      </ul>
    </nav>
  );
}
